import type WebappApp from '@/types/app';
import type { WebappWindow } from '@/types';
import { getRootDeputyFrame } from '@/global-libs/deputy-embed';

export interface DDLoggerConfiguration {
  level?: 'debug' | 'info' | 'warn' | 'error';
  handler?: 'http' | 'console' | 'silent';
  context?: Record<string, any>;
}

// Note that this is temporarily type file until we do client side injection
// rather than php template
interface DatadogLog {
  init: (config: DDLogsConfig) => void;
  logger: DDLogger;
  setGlobalContextProperty: (key: any, value: any) => void;
  createLogger: (name: string, conf?: DDLoggerConfiguration) => DDLogger;
  getLogger: (name: string) => DDLogger | undefined;
  setGlobalContext: (context: any) => void;
}

interface DDLogsConfig {
  clientToken: string;
  site?: string;
  forwardErrorsToLogs?: boolean;
  sampleRate?: number;
  // Add other configuration options as needed
}

export interface DDLogger {
  debug: (message: string, messageContext?: any, error?: any) => void;
  info: (message: string, messageContext?: any, error?: any) => void;
  warn: (message: string, messageContext?: any, error?: any) => void;
  error: (message: string, messageContext?: any, error?: any) => void;
  addTag: (key: string, value?: string) => void;
  // Add other types as needed
}

export function getWindow(): WebappWindow {
  return getRootDeputyFrame();
}

/**
 * Gets the __APP__ instance from the ROOT deputy frame
 *
 * @return {WebappApp}
 */
export function getAppInstance(): WebappApp {
  const win: WebappWindow = getRootDeputyFrame();
  win.__APP__ = win.__APP__ || ({} as WebappApp);
  return win.__APP__;
}

/**
 * Gets the __APP__ instance from the current frame
 *
 * @return {WebappApp}
 */
export function getAppInstanceFromContext(): WebappApp {
  window.__APP__ = window.__APP__ || ({} as WebappApp);
  return window.__APP__;
}

export function setAppProperty<T>(
  property: keyof WebappApp,
  value: T | unknown,
  context?: WebappApp
): void {
  if (!property) {
    console.error('property is required');
    return;
  }

  const appInstance: WebappApp = context ?? getAppInstance();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error:
  appInstance[property] = value;
}
